/**
 * FILE-OVERVIEW:
 * - 役割: カレンダー操作（保存・削除・ドラッグ更新）の処理を集約。
 * - 主な連携: components/Calendar/CalendarMain.js, services/calendarApi.js
 * - 本番運用上の注意: 更新順序を変えるとUI表示とDB状態が不整合になる可能性がある。
 */

import {
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent
} from '../services/calendarApi';
import { logInfo, logError } from '../services/appLogger';
import type { EventClickArg, EventDropArg } from '@fullcalendar/core';
import type { DateClickArg } from '@fullcalendar/interaction';
import type { CalendarEvent } from '../services/calendarApi';
import type { AuthUser } from '../hooks/useAuth';
import type { useCalendarEvents } from '../hooks/useCalendarEvents';
import type { CalendarStateApi } from './eventHelpers';
import { asErrorLike } from './errorLike';

/** useCalendarEvents が返すイベント状態 */
export type CalendarEventsApi = ReturnType<typeof useCalendarEvents>;

/** 日時文字列。API/画面のどちらから来ても受けられるようにしている */
type DateTimeInput = string | Date | null | undefined;

const isReadOnlyUser = (currentUser: AuthUser | null | undefined): boolean => (
  (currentUser?.groups || []).some((g) => String(g || '').toLowerCase() === 'read_only')
);

const formatDateOnly = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatDateTimeLocal = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${min}`;
};

const hasTimePart = (value: unknown): boolean => typeof value === 'string' && value.includes('T');

const parseDateTimeParts = (value: DateTimeInput, fallbackTime: string): { date: string; time: string } => {
  if (!value) return { date: '', time: fallbackTime };
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4}-\d{2}-\d{2})(?:T(\d{2}:\d{2}))?/);
    if (m) {
      return {
        date: m[1]!,
        time: m[2] || fallbackTime,
      };
    }
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: '', time: fallbackTime };
  return {
    date: formatDateOnly(date),
    time: `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`,
  };
};

const isUnsetTime = (value?: string): boolean => !value || value === '未定';

const combineDateTime = (date: string, time: string, fallbackTime: string): string => {
  if (!date) return '';
  if (isUnsetTime(time)) return date;
  const safeTime = time || fallbackTime;
  return `${date}T${safeTime}`;
};

const normalizeDateTimeValue = (value: DateTimeInput, fallbackTime: string): string => {
  if (!value) return '';
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return value;
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return isUnsetTime(fallbackTime) ? value : `${value}T${fallbackTime}`;
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return isUnsetTime(fallbackTime) ? formatDateOnly(date) : formatDateTimeLocal(date);
};

const parseDateOnlyLocal = (value: unknown): Date | null => {
  const m = typeof value === 'string' ? value.match(/^(\d{4})-(\d{2})-(\d{2})$/) : null;
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0);
};

/** FullCalendar がドラッグ時に渡す差分 */
interface CalendarDelta {
  years?: number;
  months?: number;
  days?: number;
  milliseconds?: number;
  /** 旧APIの別名 */
  ms?: number;
}

const applyCalendarDelta = (baseDate: Date, delta?: CalendarDelta | null): Date => {
  const d = new Date(baseDate.getTime());
  if (!delta) return d;

  const years = Number(delta.years || 0);
  const months = Number(delta.months || 0);
  const days = Number(delta.days || 0);
  const milliseconds = Number(delta.milliseconds ?? delta.ms ?? 0);

  if (years) d.setFullYear(d.getFullYear() + years);
  if (months) d.setMonth(d.getMonth() + months);
  if (days) d.setDate(d.getDate() + days);
  if (milliseconds) d.setTime(d.getTime() + milliseconds);

  return d;
};

/**
 * 日付セルクリック時の新規作成ハンドラを生成する。
 *
 * @param {Object} calendarState カレンダーUI状態
 * @returns {(arg:{dateStr:string}) => void}
 */
export const createDateClickHandler = (calendarState: CalendarStateApi) => {
  return (arg: DateClickArg) => {
    calendarState.setDefaultEvent(arg.dateStr);
    calendarState.setDialogMode('schedule');
    calendarState.setOpen(true);
  };
};

/**
 * イベントクリック時の編集ダイアログ表示ハンドラを生成する。
 *
 * @param {Object} calendarState カレンダーUI状態
 * @param {{events:Array}} calendarEvents ローカルイベント一覧
 * @param {Object|null} currentUser ログインユーザー
 * @returns {(info:Object) => void}
 *
 * 振る舞い:
 * - プライベート予定は所有者のみ編集可能。
 * - 既存 state から対象イベントを参照し、再取得なしで即時表示する。
 */
export const createEventClickHandler = (
  calendarState: CalendarStateApi,
  calendarEvents: CalendarEventsApi,
  currentUser: AuthUser | null,
) => {
  return (info: EventClickArg) => {
    try {
      const clickedEvent = calendarEvents?.events?.find(event => String(event.id) === String(info.event.id));
      if (!clickedEvent) return;

      const isPrivate = String(clickedEvent.category || '').trim() === 'プライベート'
        || clickedEvent.isPrivate === true
        || clickedEvent.isPrivate === 'true'
        || clickedEvent.private === true
        || clickedEvent.private === 'true';
      const ownerUserId = String(clickedEvent.createdBy || '').trim();
      const loginUserId = String(currentUser?.userId || '').trim();
      if (isPrivate && ownerUserId && ownerUserId !== loginUserId) {
        return;
      }

      const parsedStart = parseDateTimeParts(clickedEvent.start, '未定');
      const parsedEnd = parseDateTimeParts(clickedEvent.end || clickedEvent.start, '未定');
      calendarState.setTempEvent({
        id: clickedEvent.id,
        title: clickedEvent.title || '',
        notes: clickedEvent.notes || '',
        start: normalizeDateTimeValue(clickedEvent.start, '未定'),
        end: normalizeDateTimeValue(clickedEvent.end || clickedEvent.start, '未定'),
        startTime: parsedStart.time,
        endTime: parsedEnd.time,
        createdBy: clickedEvent.createdBy || '',
        createdByUsername: clickedEvent.createdByUsername || '',
        category: clickedEvent.category || '社会',
        person: clickedEvent.person || '',
        area: clickedEvent.area || '本社'
      });
      calendarState.setDialogMode('schedule');
      calendarState.setOpen(true);
    } catch (error) {
      console.error('スケジュール情報の取得エラー:', error);
    }
  };
};

/**
 * ドラッグ&ドロップ移動時の更新ハンドラを生成する。
 *
 * @param {{events:Array,setEvents:Function}} calendarEvents イベント状態
 * @param {Object|null} currentUser ログインユーザー
 * @returns {(info:Object) => Promise<void>}
 *
 * 振る舞い:
 * - ローカル状態を先に更新し、その後 API 更新を実行する。
 * - 非所有者のプライベート予定は revert して移動不可にする。
 */
export const createEventDropHandler = (
  calendarEvents: CalendarEventsApi,
  currentUser: AuthUser | null,
) => {
  return async (info: EventDropArg) => {
    if (isReadOnlyUser(currentUser)) {
      info.revert();
      return;
    }

    try {
      const loadedEvents = calendarEvents.events;
      const droppedEvent = loadedEvents.find(event => String(event.id) === String(info.event.id));

      if (droppedEvent) {
        const isPrivate = String(droppedEvent.category || '').trim() === 'プライベート'
          || droppedEvent.isPrivate === true
          || droppedEvent.isPrivate === 'true'
          || droppedEvent.private === true
          || droppedEvent.private === 'true';
        const ownerUserId = String(droppedEvent.createdBy || '').trim();
        const loginUserId = String(currentUser?.userId || '').trim();
        if (isPrivate && ownerUserId && ownerUserId !== loginUserId) {
          info.revert();
          return;
        }

        const originalHasTime = hasTimePart(droppedEvent.start) || hasTimePart(droppedEvent.end);
        let updatedEvent: CalendarEvent;

        if (originalHasTime) {
          const normalizedStart = normalizeDateTimeValue(droppedEvent.start, '00:00');
          const normalizedEnd = normalizeDateTimeValue(droppedEvent.end || droppedEvent.start, '00:00');
          const originalStartDate = normalizedStart ? new Date(normalizedStart) : null;
          const originalEndDate = normalizedEnd ? new Date(normalizedEnd) : originalStartDate;
          if (!originalStartDate || Number.isNaN(originalStartDate.getTime())) return;

          const shiftedStart = applyCalendarDelta(originalStartDate, info.delta);
          const shiftedEnd = originalEndDate ? applyCalendarDelta(originalEndDate, info.delta) : shiftedStart;

          updatedEvent = {
            ...droppedEvent,
            start: formatDateTimeLocal(shiftedStart),
            end: formatDateTimeLocal(shiftedEnd),
          };
        } else {
          const originalStartDate = parseDateOnlyLocal(droppedEvent.start);
          const originalEndDate = parseDateOnlyLocal(droppedEvent.end || droppedEvent.start) || originalStartDate;
          if (!originalStartDate || Number.isNaN(originalStartDate.getTime())) return;

          const shiftedStart = applyCalendarDelta(originalStartDate, info.delta);
          const shiftedEnd = originalEndDate ? applyCalendarDelta(originalEndDate, info.delta) : shiftedStart;

          updatedEvent = {
            ...droppedEvent,
            start: formatDateOnly(shiftedStart),
            end: formatDateOnly(shiftedEnd),
          };
        }

        const updatedEvents = loadedEvents.map(event =>
          event.id === updatedEvent.id ? updatedEvent : event
        );
        calendarEvents.setEvents(updatedEvents);

        await updateCalendarEvent(String(updatedEvent.id), updatedEvent);
      }
    } catch (error) {
      console.error('スケジュール移動エラー:', error);
    }
  };
};

/**
 * イベント削除ハンドラを生成する。
 *
 * @param {Object} calendarState カレンダーUI状態
 * @param {{events:Array,setEvents:Function}} calendarEvents イベント状態
 * @returns {() => Promise<void>}
 *
 * 副作用:
 * - confirm ダイアログを表示し、承認時のみ API 削除を実行する。
 */
export const createDeleteHandler = (
  calendarState: CalendarStateApi,
  calendarEvents: CalendarEventsApi,
  currentUser: AuthUser | null,
) => {
  return async () => {
    if (isReadOnlyUser(currentUser)) {
      alert('このユーザー権限では削除できません（閲覧のみ）。');
      return;
    }

    if (calendarState.isSaving || !calendarState.tempEvent.id) return;
    
    if (!window.confirm('このスケジュールを削除してもよろしいですか？')) return;
    
    calendarState.setIsSaving(true);
    
    try {
      const deletedTitle = calendarState.tempEvent?.title || '(タイトルなし)';
      const actor = currentUser?.username || currentUser?.name || '不明ユーザー';
      const result = await deleteCalendarEvent(String(calendarState.tempEvent.id));
      if (result === false) {
        throw new Error('削除処理に失敗しました');
      }
      
      calendarEvents.setEvents(calendarEvents.events.filter(event => 
        String(event.id) !== String(calendarState.tempEvent.id)
      ));
      await logInfo(
        'schedule_delete_success',
        {
          scheduleId: String(calendarState.tempEvent.id),
          title: deletedTitle,
        },
        `${actor}がスケジュール「${deletedTitle}」を削除しました。`
      );
      calendarState.setOpen(false);
    } catch (error) {
      const deletedTitle = calendarState.tempEvent?.title || '(タイトルなし)';
      const actor = currentUser?.username || currentUser?.name || '不明ユーザー';
      await logError(
        'schedule_delete_failed',
        {
          scheduleId: String(calendarState.tempEvent?.id || ''),
          title: deletedTitle,
          reason: asErrorLike(error).message || 'unknown_error',
        },
        `${actor}のスケジュール削除に失敗しました。`
      );
      alert('削除処理に失敗しました');
      console.error('削除エラー:', error);
    } finally {
      calendarState.setIsSaving(false);
    }
  };
};

/**
 * 新規作成/更新を共通で処理する保存ハンドラを生成する。
 *
 * @param {Object} calendarState カレンダーUI状態
 * @param {{events:Array,setEvents:Function}} calendarEvents イベント状態
 * @param {Object|null} currentUser ログインユーザー
 * @returns {() => Promise<void>}
 *
 * 振る舞い:
 * - 入力値検証（必須・日付前後・時刻整合）を先に実施する。
 * - 既存IDがあれば更新、なければ新規作成。
 * - 成功時はローカル state を同期してダイアログを閉じる。
 */
export const createSaveHandler = (
  calendarState: CalendarStateApi,
  calendarEvents: CalendarEventsApi,
  currentUser: AuthUser | null,
) => {
  return async () => {
    if (isReadOnlyUser(currentUser)) {
      alert('このユーザー権限では保存できません（閲覧のみ）。');
      return;
    }

    if (calendarState.isSaving) return;
    calendarState.setIsSaving(true);

    try {
      const { tempEvent } = calendarState;
      const startDatePart = parseDateTimeParts(tempEvent.start, '未定').date;
      const endDatePart = parseDateTimeParts(tempEvent.end, '未定').date;
      const startTimePart = tempEvent.startTime || '未定';
      const endTimePart = tempEvent.endTime || '未定';
      const normalizedStart = normalizeDateTimeValue(
        combineDateTime(startDatePart, startTimePart, '未定'),
        '未定'
      );
      const normalizedEnd = normalizeDateTimeValue(
        combineDateTime(endDatePart, endTimePart, '未定'),
        '未定'
      );
      
      // 入力値検証
      if (!normalizedStart || !normalizedEnd) {
        alert('開始日時と終了日時は必須です');
        return;
      }

      // 日付の前後関係は時刻の有無に関わらず常に検証する
      if (startDatePart && endDatePart && startDatePart > endDatePart) {
        alert("終了日は開始日より後の日付を選択してください。");
        return;
      }

      const isSameDate = startDatePart && endDatePart && startDatePart === endDatePart;
      const isMidnightEdgeCase = isSameDate && (
        (startTimePart === '00:00' && isUnsetTime(endTimePart)) ||
        (isUnsetTime(startTimePart) && endTimePart === '00:00')
      );
      if (isMidnightEdgeCase) {
        alert("終了時刻は開始時刻より後にしてください。");
        return;
      }

      // 時刻が両方とも設定されている場合は時刻の前後関係も検証する
      if (
        !isUnsetTime(startTimePart) &&
        !isUnsetTime(endTimePart) &&
        new Date(normalizedStart) > new Date(normalizedEnd)
      ) {
        alert("終了時刻は開始時刻より後にしてください。");
        return;
      }

      if (tempEvent.id) {
        // 更新処理
        const { startTime, endTime, ...eventWithoutUiTimes } = tempEvent;
        const ownerUserId = tempEvent.createdBy || eventWithoutUiTimes.createdBy || currentUser?.userId;
        const ownerUsername = tempEvent.createdByUsername || eventWithoutUiTimes.createdByUsername || currentUser?.username;
        const updatedEvent = {
          ...eventWithoutUiTimes,
          start: normalizedStart,
          end: normalizedEnd,
          date: normalizedStart,
          title: tempEvent.title || '',
          notes: tempEvent.notes || '',
          person: tempEvent.person || '',
          category: tempEvent.category || '社会',
          area: tempEvent.area || '本社',
          createdBy: ownerUserId,
          createdByUsername: ownerUsername,
          updatedBy: currentUser?.userId,
          updatedByUsername: currentUser?.username
        };
        
        await updateCalendarEvent(String(tempEvent.id), updatedEvent);
        calendarEvents.setEvents(calendarEvents.events.map(event =>
          event.id === tempEvent.id ? updatedEvent : event
        ));
        const actor = currentUser?.username || currentUser?.name || '不明ユーザー';
        await logInfo(
          'schedule_update_success',
          {
            scheduleId: String(tempEvent.id),
            title: updatedEvent.title || '(タイトルなし)',
            start: updatedEvent.start,
            end: updatedEvent.end,
            category: updatedEvent.category,
          },
          `${actor}がスケジュール「${updatedEvent.title || '(タイトルなし)'}」を更新しました。`
        );
        
      } else {
        // 新規作成処理
        const { startTime, endTime, ...eventWithoutUiTimes } = tempEvent;
        const newEvent = {
          ...eventWithoutUiTimes,
          start: normalizedStart,
          end: normalizedEnd,
          date: normalizedStart,
          title: tempEvent.title || '',
          notes: tempEvent.notes || '',
          person: tempEvent.person || '',
          category: tempEvent.category || '社会',
          area: tempEvent.area || '本社',
          createdBy: currentUser?.userId,
          createdByUsername: currentUser?.username
        };
        
        // 重複チェック
        const isDuplicate = calendarEvents.events.some(event =>
          event.title === newEvent.title &&
          event.notes === newEvent.notes &&
          event.person === newEvent.person &&
          event.start === newEvent.start &&
          event.end === newEvent.end &&
          event.category === newEvent.category &&
          event.area === newEvent.area
        );
        
        if (isDuplicate) {
          alert('同じ内容のスケジュールが既に登録されています');
          return;
        }

        const savedEvent = await createCalendarEvent(newEvent);
        calendarEvents.setEvents([...calendarEvents.events, savedEvent]);
        const actor = currentUser?.username || currentUser?.name || '不明ユーザー';
        await logInfo(
          'schedule_create_success',
          {
            scheduleId: String(savedEvent?.id || ''),
            title: newEvent.title || '(タイトルなし)',
            start: newEvent.start,
            end: newEvent.end,
            category: newEvent.category,
          },
          `${actor}がスケジュール「${newEvent.title || '(タイトルなし)'}」を追加しました。`
        );
      }
      
      calendarState.setOpen(false);
      
    } catch (error) {
      const actor = currentUser?.username || currentUser?.name || '不明ユーザー';
      await logError(
        'schedule_save_failed',
        {
          scheduleId: String(calendarState?.tempEvent?.id || ''),
          title: calendarState?.tempEvent?.title || '(タイトルなし)',
          reason: asErrorLike(error).message || 'unknown_error',
        },
        `${actor}のスケジュール保存に失敗しました。`
      );
      alert('保存に失敗しました');
      console.error('保存エラー:', error);
    } finally {
      calendarState.setIsSaving(false);
    }
  };
};